import { useRef, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { fi } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { DAY } from '@/constants/testIds';

const WEEKDAY_FI = ['Sunnuntai', 'Maanantai', 'Tiistai', 'Keskiviikko', 'Torstai', 'Perjantai', 'Lauantai'];

function labelFor(dateStr) {
  const d = parseISO(dateStr);
  const dd = d.getDate();
  const mm = d.getMonth() + 1;
  return `${dd}.${mm}.`;
}

export default function DayTabs({ days, current, onChange }) {
  const scrollerRef = useRef(null);
  const activeRef = useRef(null);

  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [current]);

  const idx = days.indexOf(current);
  const goPrev = () => idx > 0 && onChange(days[idx - 1]);
  const goNext = () => idx >= 0 && idx < days.length - 1 && onChange(days[idx + 1]);

  const currentDate = current ? parseISO(current) : new Date();
  const weekday = WEEKDAY_FI[currentDate.getDay()];

  return (
    <div className="space-y-3">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs tracking-[0.15em] uppercase text-[#6A6D66] font-semibold">{weekday}</p>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-[#1C1C1A]">
            {current ? format(currentDate, "d. MMMM yyyy", { locale: fi }) : '—'}
          </h1>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={goPrev}
            data-testid={DAY.prev}
            className="w-9 h-9 rounded-full border border-[#E5E6E3] bg-white hover:bg-[#F4F6F3] flex items-center justify-center text-[#1C1C1A] transition-colors"
            aria-label="Edellinen"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={goNext}
            data-testid={DAY.next}
            className="w-9 h-9 rounded-full border border-[#E5E6E3] bg-white hover:bg-[#F4F6F3] flex items-center justify-center text-[#1C1C1A] transition-colors"
            aria-label="Seuraava"
          >
            <ChevronRight size={18} />
          </button>
          <Popover>
            <PopoverTrigger asChild>
              <button
                data-testid={DAY.calendarTrigger}
                className="h-9 px-3 rounded-full border border-[#E5E6E3] bg-white hover:bg-[#F4F6F3] flex items-center gap-2 text-sm text-[#1C1C1A] transition-colors"
              >
                <CalendarDays size={16} />
                <span>Valitse päivä</span>
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto p-0">
              <Calendar
                mode="single"
                selected={currentDate}
                onSelect={(d) => d && onChange(format(d, 'yyyy-MM-dd'))}
                locale={fi}
                weekStartsOn={1}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div ref={scrollerRef} className="flex space-x-2 overflow-x-auto pb-2 -mx-2 px-2 scrollbar-thin">
        {days.map((d) => {
          const active = d === current;
          return (
            <button
              key={d}
              ref={active ? activeRef : null}
              data-testid={`${DAY.tabPrefix}${d}`}
              onClick={() => onChange(d)}
              className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                active
                  ? 'bg-[#2A4B30] text-white shadow-sm'
                  : 'bg-white border border-[#E5E6E3] text-[#6A6D66] hover:bg-[#F4F6F3] hover:text-[#1C1C1A]'
              }`}
            >
              {labelFor(d)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
